import React, { useState, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Form } from 'react-bootstrap';
import Select from 'react-select';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SectionHeader } from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getBookingsAsync,
  selectBookingItems,
  selectBookingItemsLoadingStatus,
  selectBookingsPagination,
  selectBookingsSearchFilter,
  setSearchFilters,
  resetFilters,
} from './bookingsSlice';
import { LOADING_STATUS, LOCALES, ROLES } from '../../common/constants';
import {
  BookingType,
  bookingTypeTranslations,
} from './Constants/BookingTypeEnum';
import { useLocale } from '../../locales/useLocale';
import { checkRole } from '../auth/authSlice';
import { BookingsResultsTable } from './Components/BookingsResultsTable';
import { SelectedFilterChips } from './Components/SelectedFilterChips';
import { PopulatePagination } from '../../common/types/pagination.types';

const SearchFilterDropdown = lazy(() =>
  import('../../common/components/SearchFilterDropdown').then(module => ({
    default: module.SearchFilterDropdown,
  })),
);

const SearchBar = lazy(() =>
  import('../../common/components/SearchBar').then(module => ({
    default: module.SearchBar,
  })),
);

function Bookings() {
  const { t } = useTranslation(['common', 'bookings']);
  const { currentLocale } = useLocale();
  const dispatch = useAppDispatch();
  const bookingItems = useAppSelector(selectBookingItems);
  const searchFilter = useAppSelector(selectBookingsSearchFilter);
  const bookingItemsLoadingStatus = useAppSelector(
    selectBookingItemsLoadingStatus,
  );
  const isTravelAgencyUser = useAppSelector(checkRole([ROLES.TravelAgency]));
  const bookingsPagination = useAppSelector(selectBookingsPagination);
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const currentLanguage = currentLocale === LOCALES.Fi ? 'fi' : 'en';

  const bookingFilterPayload = {
    ...searchFilter,
    onlyAccommodationBookings: searchFilter.bookingMainType === 'accommodation',
    onlyAddOnBookings: searchFilter.bookingMainType === 'addon',
  };

  const handleInputChange = event => {
    const { target } = event;
    const { value, name } = target;
    dispatch(setSearchFilters({ ...searchFilter, [name]: value }));
  };

  const handleSearchFilterChange = (field: string, value: unknown) => {
    const formattedValue =
      value instanceof Date ? format(value, 'yyyy-MM-dd') : value;

    dispatch(
      setSearchFilters({
        ...searchFilter,
        [field]: formattedValue,
      }),
    );
  };

  const handleMultiSelectChange = (options, name) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        [name]: options ? options.map(option => option.value) : [],
      }),
    );
  };

  const navigate = useNavigate();

  const doSearch = async () => {
    setSearchSubmitted(true);
    const resultAction = await dispatch(getBookingsAsync(bookingFilterPayload));
    if (getBookingsAsync.fulfilled.match(resultAction)) {
      const { data } = resultAction.payload;
      const pagination = PopulatePagination(resultAction.payload);
      if (pagination.totalCount === 1 && data?.length === 1) {
        navigate(`/bookings/${data[0].id}`, {
          state: { backUrl: '/bookings/search-bookings' },
        });
      }
    }
  };

  const doClear = () => {
    setSearchSubmitted(false);
    dispatch(resetFilters());
  };

  const handleRemoveFilters = (filterKey: string, filterKey2: string) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        [filterKey]: null,
        [filterKey2]: null,
      }),
    );
  };

  const handleRemoveValueFilter = (filterKey: string) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        [filterKey]: filterKey === 'isCancelled' ? null : undefined,
      }),
    );
  };

  const handleClearExternalBookingsOnly = () => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        externalBookingsOnly: undefined,
      }),
    );
  };

  const handleRemoveExternalSource = (source: string) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        externalBookingSources: searchFilter.externalBookingSources.filter(
          s => s !== source,
        ),
      }),
    );
  };

  const bookingTypeOptions = Object.keys(BookingType)
    .filter(
      key =>
        Number.isNaN(Number(key)) &&
        key !== 'UnConfirmedInterhome' &&
        key !== 'ConfirmedInterhome',
    )
    .map(key => ({
      value: key,
      label: bookingTypeTranslations[currentLanguage][BookingType[key]],
    }));

  const externalSourceOptions = [
    { value: 'BOOKING_COM', label: 'Booking.com' },
    { value: 'INTERHOME', label: 'Interhome' },
    {
      value: 'INTERHOME_YLLAKSENMATKAILU',
      label: 'Interhome Ylläksen Matkailu',
    },
    { value: 'SPORTUM', label: 'Sportum' },
  ];

  const HandlePaginationClick = (Page: number) => {
    const paginatedFiltering = {
      ...bookingFilterPayload,
      PageNumber: Page,
    };

    dispatch(setSearchFilters(paginatedFiltering));
    dispatch(getBookingsAsync(paginatedFiltering));
  };

  let bookingMainTypeLabel: string | undefined;
  if (searchFilter.bookingMainType === 'accommodation') {
    bookingMainTypeLabel = t('bookings:filters.onlyAccommodationBookings');
  } else if (searchFilter.bookingMainType === 'addon') {
    bookingMainTypeLabel = t('bookings:filters.onlyAddOnBookings');
  }

  const bookingItemsList = bookingItems ?? [];
  const hasBookingResults = bookingItemsList.length > 0;

  return (
    <>
      <Helmet>
        <title>{t('bookings:title')}</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={[t('bookings:header.breadcrumb')]}
          Title={t('bookings:header.title')}
        />
        <Suspense fallback={<div>Ladataan...</div>}>
          <SearchBar
            FreeTextOnChangeHandler={handleInputChange}
            FreeTextPlaceholder={t('bookings:filters.searchBarPlaceHolder')}
            SearchHandler={doSearch}
            IsLoading={bookingItemsLoadingStatus !== LOADING_STATUS.IDLE}
            Value={searchFilter.searchString}
          >
            {/* Search Scope Checkboxes */}
            {searchFilter.searchString && (
              <div className="px-24 pt-8 pb-8">
                <Form.Label className="mb-8 fw-semibold">
                  {t('bookings:filters.searchScope')}
                </Form.Label>
                <div className="d-flex flex-wrap gap-16">
                  <Form.Check
                    type="checkbox"
                    id="searchInBooking"
                    label={t('bookings:filters.searchInBooking')}
                    checked={searchFilter.searchInBooking}
                    onChange={e =>
                      handleSearchFilterChange(
                        'searchInBooking',
                        e.target.checked,
                      )
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="searchInAccommodation"
                    label={t('bookings:filters.searchInAccommodation')}
                    checked={searchFilter.searchInAccommodation}
                    onChange={e =>
                      handleSearchFilterChange(
                        'searchInAccommodation',
                        e.target.checked,
                      )
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="searchInCustomer"
                    label={t('bookings:filters.searchInCustomer')}
                    checked={searchFilter.searchInCustomer}
                    onChange={e =>
                      handleSearchFilterChange(
                        'searchInCustomer',
                        e.target.checked,
                      )
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="searchInBilling"
                    label={t('bookings:filters.searchInBilling')}
                    checked={searchFilter.searchInBilling}
                    onChange={e =>
                      handleSearchFilterChange(
                        'searchInBilling',
                        e.target.checked,
                      )
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="searchInAddon"
                    label={t('bookings:filters.searchInAddon')}
                    checked={searchFilter.searchInAddon}
                    onChange={e =>
                      handleSearchFilterChange(
                        'searchInAddon',
                        e.target.checked,
                      )
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="searchInNotes"
                    label={t('bookings:filters.searchInNotes')}
                    checked={searchFilter.searchInNotes}
                    onChange={e =>
                      handleSearchFilterChange(
                        'searchInNotes',
                        e.target.checked,
                      )
                    }
                  />
                </div>
              </div>
            )}
            <div className="position-relative px-24 pt-0 pb-8">
              <Suspense fallback={<div>Ladataan...</div>}>
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={false}
                  Id="bookings-filter-created"
                  Label={t('bookings:filters.bookingCreated')}
                  Value={!!searchFilter.createdFrom || !!searchFilter.createdTo}
                >
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('common:inputInstructions.dateFrom')}
                    </Form.Label>
                    <DatePicker
                      locale={currentLanguage}
                      selected={
                        searchFilter.createdFrom
                          ? new Date(searchFilter.createdFrom)
                          : null
                      }
                      dateFormat="dd.MM.yyyy"
                      onChange={date =>
                        handleSearchFilterChange('createdFrom', date)
                      }
                      showWeekNumbers
                      className="form-control"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>
                      {t('common:inputInstructions.dateTo')}
                    </Form.Label>
                    <DatePicker
                      locale={currentLanguage}
                      selected={
                        searchFilter.createdTo
                          ? new Date(searchFilter.createdTo)
                          : null
                      }
                      minDate={
                        searchFilter.createdFrom
                          ? new Date(searchFilter.createdFrom)
                          : null
                      }
                      dateFormat="dd.MM.yyyy"
                      onChange={date =>
                        handleSearchFilterChange('createdTo', date)
                      }
                      showWeekNumbers
                      className="form-control"
                      disabled={!searchFilter.createdFrom}
                    />
                  </Form.Group>
                </SearchFilterDropdown>
              </Suspense>
              <Suspense fallback={<div>Ladataan...</div>}>
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={false}
                  Id="bookings-filter-start-end-date"
                  Label={t('bookings:filters.bookingValid')}
                  Value={
                    !!searchFilter.bookingStartDate ||
                    !!searchFilter.bookingEndDate
                  }
                >
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('common:inputInstructions.dateFrom')}
                    </Form.Label>
                    <DatePicker
                      locale={currentLanguage}
                      selected={
                        searchFilter.bookingStartDate
                          ? new Date(searchFilter.bookingStartDate)
                          : null
                      }
                      dateFormat="dd.MM.yyyy"
                      onChange={date =>
                        handleSearchFilterChange('bookingStartDate', date)
                      }
                      showWeekNumbers
                      className="form-control"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>
                      {t('common:inputInstructions.dateTo')}
                    </Form.Label>
                    <DatePicker
                      locale={currentLanguage}
                      selected={
                        searchFilter.bookingEndDate
                          ? new Date(searchFilter.bookingEndDate)
                          : null
                      }
                      minDate={
                        searchFilter.bookingStartDate
                          ? new Date(searchFilter.bookingStartDate)
                          : null
                      }
                      dateFormat="dd.MM.yyyy"
                      onChange={date =>
                        handleSearchFilterChange('bookingEndDate', date)
                      }
                      showWeekNumbers
                      className="form-control"
                      disabled={!searchFilter.bookingStartDate}
                    />
                  </Form.Group>
                </SearchFilterDropdown>
              </Suspense>
              <Suspense fallback={<div>Ladataan...</div>}>
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={false}
                  Id="bookings-filter-starts"
                  Label={t('bookings:filters.bookingStarts')}
                  Value={
                    !!searchFilter.bookingStartsFrom ||
                    !!searchFilter.bookingStartsTo
                  }
                >
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('common:inputInstructions.dateFrom')}
                    </Form.Label>
                    <DatePicker
                      locale={currentLanguage}
                      selected={
                        searchFilter.bookingStartsFrom
                          ? new Date(searchFilter.bookingStartsFrom)
                          : null
                      }
                      dateFormat="dd.MM.yyyy"
                      onChange={date =>
                        handleSearchFilterChange('bookingStartsFrom', date)
                      }
                      showWeekNumbers
                      className="form-control"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>
                      {t('common:inputInstructions.dateTo')}
                    </Form.Label>
                    <DatePicker
                      locale={currentLanguage}
                      selected={
                        searchFilter.bookingStartsTo
                          ? new Date(searchFilter.bookingStartsTo)
                          : null
                      }
                      minDate={
                        searchFilter.bookingStartsFrom
                          ? new Date(searchFilter.bookingStartsFrom)
                          : null
                      }
                      dateFormat="dd.MM.yyyy"
                      onChange={date =>
                        handleSearchFilterChange('bookingStartsTo', date)
                      }
                      showWeekNumbers
                      className="form-control"
                      disabled={!searchFilter.bookingStartsFrom}
                    />
                  </Form.Group>
                </SearchFilterDropdown>
              </Suspense>
              <Suspense fallback={<div>Ladataan...</div>}>
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={false}
                  Id="bookings-filter-starts-within-days"
                  Label={t('bookings:filters.bookingStartsWithinDays')}
                  Value={!!searchFilter.bookingStartsWithinDays}
                >
                  <Form.Group>
                    <Form.Label>
                      {t('bookings:filters.numberOfDays')}
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={searchFilter.bookingStartsWithinDays ?? ''}
                      onChange={e => {
                        const { value } = e.target;
                        handleSearchFilterChange(
                          'bookingStartsWithinDays',
                          value === '' ? undefined : parseInt(value, 10),
                        );
                      }}
                    />
                  </Form.Group>
                </SearchFilterDropdown>
              </Suspense>
              <Suspense fallback={<div>Ladataan...</div>}>
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={false}
                  Id="bookings-filter-handler-names"
                  Label={t('bookings:filters.bookingHandlerName')}
                  Value={
                    !!searchFilter.bookingAuthorName ||
                    !!searchFilter.bookingCancellerName
                  }
                >
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('bookings:filters.bookingAuthorName')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={searchFilter.bookingAuthorName ?? ''}
                      onChange={e =>
                        handleSearchFilterChange(
                          'bookingAuthorName',
                          e.target.value === '' ? undefined : e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>
                      {t('bookings:filters.bookingCancellerName')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={searchFilter.bookingCancellerName ?? ''}
                      onChange={e =>
                        handleSearchFilterChange(
                          'bookingCancellerName',
                          e.target.value === '' ? undefined : e.target.value,
                        )
                      }
                    />
                  </Form.Group>
                </SearchFilterDropdown>
              </Suspense>
              <Suspense fallback={<div>Ladataan...</div>}>
                {!isTravelAgencyUser && (
                  <SearchFilterDropdown
                    ShowCloseButton={false}
                    DefaultValue={false}
                    Id="bookings-filter-main-type"
                    Label={t('bookings:filters.bookingMainType')}
                    Value={!!searchFilter.bookingMainType}
                  >
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="booking-main-type-accommodation"
                        label={t('bookings:filters.onlyAccommodationBookings')}
                        checked={
                          searchFilter.bookingMainType === 'accommodation'
                        }
                        onChange={e =>
                          dispatch(
                            setSearchFilters({
                              ...searchFilter,
                              bookingMainType: e.target.checked
                                ? 'accommodation'
                                : undefined,
                            }),
                          )
                        }
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        id="booking-main-type-addon"
                        label={t('bookings:filters.onlyAddOnBookings')}
                        checked={searchFilter.bookingMainType === 'addon'}
                        onChange={e =>
                          dispatch(
                            setSearchFilters({
                              ...searchFilter,
                              bookingMainType: e.target.checked
                                ? 'addon'
                                : undefined,
                            }),
                          )
                        }
                      />
                    </Form.Group>
                  </SearchFilterDropdown>
                )}
              </Suspense>
              {!isTravelAgencyUser && (
                <Suspense fallback={<div>Ladataan...</div>}>
                  <SearchFilterDropdown
                    ShowCloseButton={false}
                    DefaultValue={false}
                    Id="bookings-filter-external-only"
                    Label={t('bookings:filters.externalBookingsOnly')}
                    Value={!!searchFilter.externalBookingsOnly}
                  >
                    <Form.Check
                      type="checkbox"
                      id="external-bookings-only-checkbox"
                      label={t('bookings:filters.externalBookingsOnly')}
                      checked={searchFilter.externalBookingsOnly ?? false}
                      onChange={e => {
                        handleSearchFilterChange(
                          'externalBookingsOnly',
                          e.target.checked ? true : undefined,
                        );
                      }}
                    />
                  </SearchFilterDropdown>
                </Suspense>
              )}

              {!isTravelAgencyUser && (
                <Suspense fallback={<div>Ladataan...</div>}>
                  <SearchFilterDropdown
                    ShowCloseButton={false}
                    DefaultValue={false}
                    Id="bookings-filter-external-source"
                    Label={t('bookings:filters.externalBookingSource')}
                    MinWidth={300}
                    Value={searchFilter.externalBookingSources.length > 0}
                  >
                    <Select
                      isMulti
                      isClearable={false}
                      isSearchable={false}
                      inputId="externalBookingSource"
                      name="externalBookingSource"
                      placeholder={t('bookings:filters.externalBookingSources')}
                      options={externalSourceOptions}
                      onChange={options =>
                        handleMultiSelectChange(
                          options,
                          'externalBookingSources',
                        )
                      }
                      value={externalSourceOptions.filter(option =>
                        searchFilter.externalBookingSources.includes(
                          option.value,
                        ),
                      )}
                    />
                  </SearchFilterDropdown>
                </Suspense>
              )}
              <Suspense fallback={<div>Ladataan...</div>}>
                {!isTravelAgencyUser && (
                  <SearchFilterDropdown
                    DefaultValue={false}
                    Id="bookings-filter-types"
                    Label={t('bookings:filters.bookingType')}
                    MinWidth={300}
                    Value={searchFilter.bookingTypes.length > 0}
                    ShowCloseButton={false}
                  >
                    <Select
                      isMulti
                      isClearable={false}
                      isSearchable={false}
                      inputId="bookingType"
                      name="bookingType"
                      placeholder={t('bookings:filters.bookingTypes')}
                      options={bookingTypeOptions}
                      onChange={options =>
                        handleMultiSelectChange(options, 'bookingTypes')
                      }
                      value={bookingTypeOptions.filter(option =>
                        searchFilter.bookingTypes.includes(option.value),
                      )}
                    />
                  </SearchFilterDropdown>
                )}
              </Suspense>
              <Suspense fallback={<div>Ladataan...</div>}>
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={null}
                  Id="bookings-filter-iscancelled"
                  Label={t('bookings:filters.cancelled')}
                  Value={searchFilter.isCancelled}
                >
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="custom-switch"
                      label={t('bookings:filters.cancelled')}
                      checked={searchFilter.isCancelled === true}
                      onChange={e =>
                        handleSearchFilterChange(
                          'isCancelled',
                          e.target.checked,
                        )
                      }
                    />
                  </Form.Group>
                </SearchFilterDropdown>
              </Suspense>
            </div>
            <SelectedFilterChips
              searchFilter={searchFilter}
              bookingMainTypeLabel={bookingMainTypeLabel}
              externalSourceOptions={externalSourceOptions}
              onClearAll={doClear}
              onRemoveDateRange={handleRemoveFilters}
              onRemoveValue={handleRemoveValueFilter}
              onClearExternalBookingsOnly={handleClearExternalBookingsOnly}
              onRemoveExternalSource={handleRemoveExternalSource}
            />
          </SearchBar>
        </Suspense>
        {bookingItemsLoadingStatus === LOADING_STATUS.IDLE &&
          hasBookingResults && (
            <>
              <div className="mb-16">
                <p className="mb-0">
                  {bookingsPagination && bookingsPagination.totalCount
                    ? bookingsPagination.totalCount
                    : bookingItemsList.length}{' '}
                  {t('bookings:results.legend')}
                </p>
              </div>
              <BookingsResultsTable
                bookingItems={bookingItemsList}
                currentLanguage={currentLanguage}
                bookingsPagination={bookingsPagination}
                onPaginationClick={HandlePaginationClick}
              />
            </>
          )}

        {bookingItemsLoadingStatus === LOADING_STATUS.IDLE &&
          !hasBookingResults &&
          searchSubmitted && <p>{t('bookings:noResults')}</p>}
      </Container>
    </>
  );
}

export default Bookings;
